import React, { useState, useEffect } from 'react';
import { adminServices } from '../services/adminServices';
import {
  OFFER_CODE_TYPES,
  OFFER_CODE_TYPE_OPTIONS,
  resolveOfferCodeType,
  buildOfferCodePayload,
  offerCodeTypeLabel,
  isSingleUseOffer,
  offerRedemptionLabel,
  preserveOfferRedemptionFields,
} from '../constants/offerCodeTypes';

const LEVELS = ['foundation', 'certified', 'accredited'];

const emptyOffer = () => ({
  code: '',
  label: '',
  active: true,
  codeType: OFFER_CODE_TYPES.DISCOUNT,
  requirePaymentGateway: true,
  singleUse: false,
  amounts: { foundation: 0, certified: 0, accredited: 0 },
});

const AdminOffersSection = ({ canEdit, adminName }) => {
  const [offersConfig, setOffersConfig] = useState(null);
  const [draft, setDraft] = useState(emptyOffer());
  const [isSaving, setIsSaving] = useState(false);
  const [feedback, setFeedback] = useState(null);

  useEffect(() => {
    let cancelled = false;
    adminServices
      .getOffersConfig()
      .then((data) => {
        if (!cancelled) setOffersConfig(data);
      })
      .catch((err) => {
        console.error('getOffersConfig', err);
        if (!cancelled) {
          setOffersConfig({ codes: {} });
          setFeedback({
            type: 'error',
            message:
              err?.code === 'permission-denied'
                ? 'Cannot load offer codes (Firestore permission). Deploy updated firestore.rules, or sign in as approved @ifip.group staff.'
                : err?.message || 'Failed to load offer codes.',
          });
        }
      });
    return () => {
      cancelled = true;
    };
  }, []);

  const codes = offersConfig?.codes || {};
  const codeList = Object.values(codes).sort((a, b) =>
    String(a.code || '').localeCompare(String(b.code || ''))
  );

  const persistCodes = async (nextCodes) => {
    setIsSaving(true);
    setFeedback(null);
    try {
      await adminServices.saveOffersConfig({ codes: nextCodes }, adminName);
      setOffersConfig({ codes: nextCodes });
      setFeedback({ type: 'success', message: 'Offers saved.' });
    } catch (e) {
      setFeedback({ type: 'error', message: e?.message || 'Save failed.' });
    } finally {
      setIsSaving(false);
    }
  };

  const handleAddOrUpdate = async () => {
    const code = String(draft.code || '').trim().toUpperCase().replace(/\s+/g, '');
    if (!code) {
      setFeedback({ type: 'error', message: 'Code is required.' });
      return;
    }
    for (const lvl of LEVELS) {
      const n = Number(draft.amounts?.[lvl]);
      if (!Number.isFinite(n) || n < 0) {
        setFeedback({ type: 'error', message: `Enter a valid GBP amount for ${lvl}.` });
        return;
      }
    }
    const payload = buildOfferCodePayload({ ...draft, code });
    const existing = codes[code];
    const nextEntry = existing ? preserveOfferRedemptionFields(payload, existing) : payload;
    const next = {
      ...codes,
      [code]: nextEntry,
    };
    await persistCodes(next);
    setDraft(emptyOffer());
  };

  const handleToggleActive = async (entry) => {
    const key = entry.code;
    const next = {
      ...codes,
      [key]: { ...entry, active: entry.active === false },
    };
    await persistCodes(next);
  };

  const handleDelete = async (code) => {
    if (!window.confirm(`Remove offer code ${code}?`)) return;
    const next = { ...codes };
    delete next[code];
    await persistCodes(next);
  };

  const draftCodeType = resolveOfferCodeType(draft);

  if (!offersConfig) {
    return <p style={{ opacity: 0.6, fontSize: '0.85rem' }}>Loading offers…</p>;
  }

  return (
    <section className="pricing-section" style={{ marginTop: '28px' }}>
      <h3 style={{ color: 'white', margin: '0 0 8px 0' }}>Registration offer codes</h3>
      <p style={{ fontSize: '0.75rem', color: '#94a3b8', margin: '0 0 20px 0', maxWidth: '900px', lineHeight: 1.5 }}>
        Choose whether each code is a <strong>proof of purchase</strong> (partner already paid — unlocks registration without Stripe)
        or a <strong>discount code</strong> (special price via Stripe / invoice). Schools enter codes on the registration gate when required.
        {' '}Optional <strong>single-use</strong> codes can only be redeemed once; the list shows which school used them.
      </p>

      {feedback && (
        <p
          style={{
            marginBottom: '16px',
            padding: '10px 14px',
            borderRadius: '8px',
            fontSize: '0.8rem',
            border:
              feedback.type === 'error'
                ? '1px solid rgba(248,113,113,0.5)'
                : '1px solid rgba(45,212,191,0.4)',
            background:
              feedback.type === 'error' ? 'rgba(127,29,29,0.35)' : 'rgba(6,78,59,0.35)',
            color: feedback.type === 'error' ? '#fecaca' : '#a7f3d0',
          }}
        >
          {feedback.message}
        </p>
      )}

      <fieldset
        disabled={!canEdit}
        style={{
          border: 'none',
          margin: '0 0 20px 0',
          padding: 0,
        }}
      >
        <legend style={{ fontSize: '0.72rem', fontWeight: 700, color: '#cbd5e1', textTransform: 'uppercase', letterSpacing: '0.06em', marginBottom: '10px' }}>
          Code type
        </legend>
        <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(260px, 1fr))', gap: '12px' }}>
          {OFFER_CODE_TYPE_OPTIONS.map((opt) => {
            const selected = draftCodeType === opt.value;
            return (
              <label
                key={opt.value}
                style={{
                  display: 'block',
                  padding: '14px 16px',
                  borderRadius: '10px',
                  border: selected
                    ? opt.value === OFFER_CODE_TYPES.PROOF_OF_PURCHASE
                      ? '2px solid #d97706'
                      : '2px solid #138a8a'
                    : '1px solid #334155',
                  background: selected ? 'rgba(0,0,0,0.35)' : 'rgba(0,0,0,0.15)',
                  cursor: canEdit ? 'pointer' : 'default',
                }}
              >
                <input
                  type="radio"
                  name="offerCodeType"
                  value={opt.value}
                  checked={selected}
                  onChange={() =>
                    setDraft({
                      ...draft,
                      codeType: opt.value,
                      requirePaymentGateway: opt.value === OFFER_CODE_TYPES.DISCOUNT,
                    })
                  }
                  style={{ marginRight: '8px' }}
                />
                <strong style={{ color: opt.value === OFFER_CODE_TYPES.PROOF_OF_PURCHASE ? '#fbbf24' : '#5eead4', fontSize: '0.88rem' }}>
                  {opt.label}
                </strong>
                <p style={{ margin: '8px 0 0 0', fontSize: '0.72rem', lineHeight: 1.45, color: '#94a3b8' }}>{opt.description}</p>
              </label>
            );
          })}
        </div>
      </fieldset>

      <div
        style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fit, minmax(140px, 1fr))',
          gap: '12px',
          marginBottom: '16px',
          alignItems: 'end',
        }}
      >
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
          Code
          <input
            className="pricing-input"
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', padding: '8px' }}
            value={draft.code}
            onChange={(e) => setDraft({ ...draft, code: e.target.value.toUpperCase() })}
            disabled={!canEdit}
            placeholder="PARTNER-2026"
          />
        </label>
        <label style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.7rem', color: '#94a3b8', gridColumn: 'span 2' }}>
          Label (internal)
          <input
            className="pricing-input"
            style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', padding: '8px' }}
            value={draft.label}
            onChange={(e) => setDraft({ ...draft, label: e.target.value })}
            disabled={!canEdit}
            placeholder="Nigeria partner cohort — Q2 receipt"
          />
        </label>
        {LEVELS.map((lvl) => (
          <label key={lvl} style={{ display: 'flex', flexDirection: 'column', gap: '6px', fontSize: '0.7rem', color: '#94a3b8' }}>
            {lvl} (GBP)
            <div className="input-with-reset" style={{ background: '#0f172a', border: '1px solid #334155', borderRadius: '4px', padding: '0 8px' }}>
            <span className="currency-symbol">GBP</span>
            <input
              type="number"
              min="0"
              className="pricing-input"
              style={{ padding: '8px 0' }}
              value={draft.amounts[lvl]}
              onChange={(e) =>
                setDraft({
                  ...draft,
                  amounts: { ...draft.amounts, [lvl]: e.target.value },
                })
              }
              disabled={!canEdit}
            />
            </div>
          </label>
        ))}
      </div>

      <label
        style={{
          display: 'flex',
          alignItems: 'flex-start',
          gap: '10px',
          marginBottom: '16px',
          fontSize: '0.78rem',
          color: '#cbd5e1',
          cursor: canEdit ? 'pointer' : 'default',
          maxWidth: '640px',
        }}
      >
        <input
          type="checkbox"
          checked={draft.singleUse === true}
          disabled={!canEdit || !!draft.redeemedBySchoolId}
          onChange={(e) => setDraft({ ...draft, singleUse: e.target.checked })}
          style={{ marginTop: '3px' }}
        />
        <span>
          <strong>Single use</strong> — this code can only be used by one school. Leave unchecked for reusable partner /
          cohort codes (current default).
          {draft.redeemedBySchoolId && (
            <span style={{ display: 'block', marginTop: '6px', color: '#fbbf24' }}>
              Already used by {draft.redeemedSchoolName || 'a school'}; single-use cannot be turned off until usage is cleared in Firestore.
            </span>
          )}
        </span>
      </label>

      <button
        type="button"
        onClick={handleAddOrUpdate}
        disabled={!canEdit || isSaving}
        className="btn-activate"
        style={{ marginBottom: '24px' }}
      >
        {isSaving ? 'Saving…' : 'Save offer code'}
      </button>

      <table className="pricing-table">
        <thead>
          <tr>
            <th>Code</th>
            <th>Type</th>
            <th>Label</th>
            <th>Foundation (GBP)</th>
            <th>Certified (GBP)</th>
            <th>Accredited (GBP)</th>
            <th>Single use</th>
            <th>Used by</th>
            <th>Status</th>
            <th />
          </tr>
        </thead>
        <tbody>
          {codeList.length === 0 ? (
            <tr>
              <td colSpan={10} style={{ opacity: 0.5, textAlign: 'center', padding: '24px' }}>
                No offer codes yet.
              </td>
            </tr>
          ) : (
            codeList.map((entry) => {
              const type = resolveOfferCodeType(entry);
              const isProof = type === OFFER_CODE_TYPES.PROOF_OF_PURCHASE;
              return (
              <tr key={entry.code}>
                <td style={{ fontFamily: 'monospace', fontWeight: 'bold' }}>{entry.code}</td>
                <td>
                  <span
                    style={{
                      display: 'inline-block',
                      fontSize: '0.65rem',
                      fontWeight: 700,
                      textTransform: 'uppercase',
                      letterSpacing: '0.04em',
                      padding: '4px 10px',
                      borderRadius: '100px',
                      border: isProof ? '1px solid #d97706' : '1px solid #138a8a',
                      color: isProof ? '#fbbf24' : '#5eead4',
                      background: isProof ? 'rgba(217,119,6,0.15)' : 'rgba(19,138,138,0.15)',
                    }}
                  >
                    {offerCodeTypeLabel(type)}
                  </span>
                </td>
                <td style={{ fontSize: '0.85rem', opacity: 0.85 }}>{entry.label || '—'}</td>
                {LEVELS.map((lvl) => (
                  <td key={lvl}>GBP {Number(entry.amounts?.[lvl] || 0).toLocaleString('en-GB')}</td>
                ))}
                <td>{isSingleUseOffer(entry) ? 'Yes' : '—'}</td>
                <td style={{ fontSize: '0.85rem', maxWidth: '180px' }}>
                  {offerRedemptionLabel(entry) || (isSingleUseOffer(entry) ? 'Available' : '—')}
                </td>
                <td>{entry.active === false ? 'Inactive' : 'Active'}</td>
                <td style={{ whiteSpace: 'nowrap' }}>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginRight: '8px', fontSize: '0.7rem', padding: '4px 10px' }}
                    disabled={!canEdit || isSaving}
                    onClick={() =>
                      setDraft({
                        ...entry,
                        codeType: resolveOfferCodeType(entry),
                        singleUse: isSingleUseOffer(entry),
                        amounts: { ...entry.amounts },
                      })
                    }
                  >
                    Edit
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ marginRight: '8px', fontSize: '0.7rem', padding: '4px 10px' }}
                    disabled={!canEdit || isSaving}
                    onClick={() => handleToggleActive(entry)}
                  >
                    {entry.active === false ? 'Activate' : 'Deactivate'}
                  </button>
                  <button
                    type="button"
                    className="btn-secondary"
                    style={{ fontSize: '0.7rem', padding: '4px 10px', color: '#f87171' }}
                    disabled={!canEdit || isSaving}
                    onClick={() => handleDelete(entry.code)}
                  >
                    Remove
                  </button>
                </td>
              </tr>
            );
            })
          )}
        </tbody>
      </table>
    </section>
  );
};

export default AdminOffersSection;
